import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import Select from 'react-select';
import DashboardLayout from './DashboardLayout';
import { useErrorAlert, useInfoAlert } from './AlertSystem';
import { reportsApi } from '../lib/api';
import './DashboardReports.css';

interface Report {
    id: number;
    title: string;
    description: string;
    content: string;
    platform?: string;
    threatType?: string;
    postedBy?: string;
    dateDetected?: string;
    affectedCompany?: string;
    dataType?: string;
    proofShared: boolean;
    threatScore?: number | string | null;
    tags?: string[];
    downloadUrl?: string;
    views: number;
    downloads: number;
    createdAt: string;
    updatedAt: string;
    createdBy?: {
        id: number;
        name: string;
        email?: string;
    };
}

interface OptionType {
    value: string;
    label: string;
}

const DashboardReports: React.FC = () => {
    // Alert system hooks
    const showError = useErrorAlert();
    const showInfo = useInfoAlert();
    
    // Create refs to avoid dependency issues
    const showErrorRef = useRef(showError);
    const showInfoRef = useRef(showInfo);
    
    // Update refs when functions change
    useEffect(() => {
        showErrorRef.current = showError;
        showInfoRef.current = showInfo;
    }, [showError, showInfo]);

    const [allReports, setAllReports] = useState<Report[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
    const [platformFilter, setPlatformFilter] = useState('all');
    const [threatTypeFilter, setThreatTypeFilter] = useState('all');
    const [selectedReport, setSelectedReport] = useState<Report | null>(null);
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [error, setError] = useState<string | null>(null);
    const [isFiltering, setIsFiltering] = useState(false);
    const [platformOptions, setPlatformOptions] = useState<OptionType[]>([{ value: 'all', label: 'All Platforms' }]);
    const [threatTypeOptions, setThreatTypeOptions] = useState<OptionType[]>([{ value: 'all', label: 'All Threat Types' }]);
    const reportsPerPage = 10;

    // Custom styles for react-select
    const selectStyles = {
        control: (provided: any, state: any) => ({
            ...provided,
            background: 'rgba(26, 26, 39, 0.8)',
            border: `1px solid ${state.isFocused ? 'var(--primary-purple)' : 'rgba(148, 163, 184, 0.2)'}`,
            borderRadius: '8px',
            padding: '2px 4px',
            color: 'var(--text-primary)',
            fontSize: '0.875rem',
            fontFamily: "'Inter', 'Chivo', sans-serif",
            minHeight: '40px',
            boxShadow: state.isFocused ? '0 0 0 2px rgba(99, 102, 241, 0.15)' : 'none',
            cursor: 'pointer',
            '&:hover': {
                border: `1px solid ${state.isFocused ? 'var(--primary-purple)' : 'rgba(99, 102, 241, 0.4)'}`
            }
        }),
        menu: (provided: any) => ({
            ...provided,
            background: 'rgba(26, 26, 39, 0.95)',
            border: '1px solid rgba(99, 102, 241, 0.3)',
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
            zIndex: 99999999,
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)'
        }),
        option: (provided: any, state: any) => ({
            ...provided,
            background: state.isSelected 
                ? 'rgba(99, 102, 241, 0.2)' 
                : state.isFocused 
                ? 'rgba(255, 255, 255, 0.08)' 
                : 'transparent',
            color: state.isSelected ? 'var(--primary-purple)' : 'var(--text-primary)',
            fontSize: '0.875rem',
            fontFamily: "'Inter', 'Chivo', sans-serif",
            padding: '10px 14px',
            cursor: 'pointer',
            '&:hover': {
                background: state.isSelected ? 'rgba(99, 102, 241, 0.3)' : 'rgba(255, 255, 255, 0.08)'
            }
        }),
        singleValue: (provided: any) => ({
            ...provided,
            color: 'var(--text-primary)',
            fontSize: '0.875rem',
            fontFamily: "'Inter', 'Chivo', sans-serif"
        }),
        placeholder: (provided: any) => ({
            ...provided,
            color: 'var(--text-muted)',
            fontSize: '0.875rem',
            fontFamily: "'Inter', 'Chivo', sans-serif"
        }),
        input: (provided: any) => ({
            ...provided,
            color: 'var(--text-primary)',
            fontSize: '0.875rem',
            fontFamily: "'Inter', 'Chivo', sans-serif"
        }),
        dropdownIndicator: (provided: any) => ({
            ...provided,
            color: 'var(--text-muted)',
            padding: '6px',
            '&:hover': {
                color: 'var(--text-primary)'
            }
        }),
        indicatorSeparator: () => ({
            display: 'none'
        })
    };

    // Debounce search query
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery);
        }, 300);

        return () => clearTimeout(timer);
    }, [searchQuery]);

    // All filtering is done server-side for better performance
    // allReports already contains the filtered results from the server
    const filteredReports = useMemo(() => {
        return allReports;
    }, [allReports]);

    // Pagination
    const totalPages = Math.ceil(filteredReports.length / reportsPerPage);
    const startIndex = (currentPage - 1) * reportsPerPage;
    const paginatedReports = filteredReports.slice(startIndex, startIndex + reportsPerPage);

    // Reset to first page when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearchQuery, platformFilter, threatTypeFilter]);

    // Fetch filter options from API
    const fetchOptions = useCallback(async () => {
        try {
            const response = await reportsApi.getOptions();
            if (response.success && response.data) {
                const platforms = [{ value: 'all', label: 'All Platforms' }, ...response.data.platforms];
                const threatTypes = [{ value: 'all', label: 'All Threat Types' }, ...response.data.threatTypes];
                
                setPlatformOptions(platforms);
                setThreatTypeOptions(threatTypes);
            }
        } catch (error) {
            console.error('Error fetching options:', error);
            // Use fallback options if API fails
            setPlatformOptions([
                { value: 'all', label: 'All Platforms' },
                { value: 'TELEGRAM', label: 'Telegram' },
                { value: 'DISCORD', label: 'Discord' },
                { value: 'DARKWEB', label: 'Dark Web' },
                { value: 'FORUM', label: 'Forum' },
                { value: 'SOCIAL_MEDIA', label: 'Social Media' },
                { value: 'MARKETPLACE', label: 'Marketplace' },
                { value: 'EMAIL', label: 'Email' },
                { value: 'WEBSITE', label: 'Website' },
                { value: 'OTHER', label: 'Other' }
            ]);
            setThreatTypeOptions([
                { value: 'all', label: 'All Threat Types' },
                { value: 'DATA_BREACH', label: 'Data Breach' },
                { value: 'MALWARE', label: 'Malware' },
                { value: 'PHISHING', label: 'Phishing' },
                { value: 'RANSOMWARE', label: 'Ransomware' },
                { value: 'CREDENTIAL_THEFT', label: 'Credential Theft' },
                { value: 'FINANCIAL_FRAUD', label: 'Financial Fraud' },
                { value: 'VULNERABILITY', label: 'Vulnerability' },
                { value: 'SOCIAL_ENGINEERING', label: 'Social Engineering' },
                { value: 'DDOS_ATTACK', label: 'DDoS Attack' },
                { value: 'INSIDER_THREAT', label: 'Insider Threat' },
                { value: 'APT', label: 'Advanced Persistent Threat' },
                { value: 'OTHER', label: 'Other' }
            ]);
        }
    }, []);

    // Initial data fetch
    const fetchReports = useCallback(async () => {
        try {
            setLoading(true);
            setIsFiltering(true);
            setError(null);

            // Pass current filters to the API for server-side filtering
            const params: any = { 
                limit: 1000,
                search: debouncedSearchQuery
            };

            // Only add filters if they're not 'all'
            if (platformFilter !== 'all') {
                params.platform = platformFilter;
            }
            if (threatTypeFilter !== 'all') {
                params.threatType = threatTypeFilter;
            }

            console.log('Fetching reports with params:', params); // Debug log

            const response = await reportsApi.getReports(params);

            if (response.success && response.data) {
                const reports = response.data.reports || [];
                setAllReports(reports);
                
                // Debug: Log unique platform and threat type values
                const platforms = Array.from(new Set(reports.map((r: Report) => r.platform).filter(Boolean)));
                const threatTypes = Array.from(new Set(reports.map((r: Report) => r.threatType).filter(Boolean)));
                console.log('Available platforms in data:', platforms);
                console.log('Available threat types in data:', threatTypes);
                console.log('Current filters - Platform:', platformFilter, 'ThreatType:', threatTypeFilter);
                console.log('Loaded', reports.length, 'reports');
            } else {
                throw new Error(response.error || 'Failed to fetch reports');
            }
        } catch (error) {
            console.error('Error fetching reports:', error);
            setError(error instanceof Error ? error.message : 'Failed to fetch reports');
            showErrorRef.current(error instanceof Error ? error.message : 'Failed to fetch reports', 'Error Loading Reports');
            setAllReports([]);
        } finally {
            setLoading(false);
            setIsFiltering(false);
        }
    }, [debouncedSearchQuery, platformFilter, threatTypeFilter]);

    useEffect(() => {
        fetchOptions();
        fetchReports();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchReports]); // Fetch when filters change

    // Fetch options on component mount
    useEffect(() => {
        fetchOptions();
    }, [fetchOptions]);

    const formatDate = (dateString: string) => {
        if (!dateString) return 'Never';
        
        const date = new Date(dateString);
        // Ensure we're displaying in the user's local timezone
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            timeZoneName: 'short'
        });
    };

    const getTimeAgo = (dateString: string) => {
        if (!dateString) return 'Never';
        
        const date = new Date(dateString);
        const now = new Date();
        
        // Calculate difference in UTC to avoid timezone issues
        const diffInMs = now.getTime() - date.getTime();
        const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
        const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
        const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

        if (diffInMinutes < 1) {
            return 'Just now';
        } else if (diffInMinutes < 60) {
            return `${diffInMinutes}m ago`;
        } else if (diffInHours < 24) {
            return `${diffInHours}h ago`;
        } else if (diffInDays < 7) {
            return `${diffInDays}d ago`;
        } else {
            return formatDate(dateString);
        }
    };

    const getThreatScoreColor = (score: number | null) => {
        if (!score) return '#6b7280';
        if (score >= 8) return '#ef4444';
        if (score >= 6) return '#f59e0b';
        if (score >= 4) return '#3b82f6';
        return '#10b981';
    };

    // Helper function to safely convert threatScore to number
    const getSafeScore = (score?: number | string | null): number | null => {
        if (score === null || score === undefined) return null;
        if (typeof score === 'number') return score;
        if (typeof score === 'string') {
            const parsed = parseFloat(score);
            return isNaN(parsed) ? null : parsed;
        }
        return null;
    };

    const getPlatformIcon = (platform: string | null) => {
        switch (platform) {
            case 'TELEGRAM':
            case 'telegram': return 'ri-telegram-line';
            case 'DISCORD':
            case 'discord': return 'ri-discord-line';
            case 'DARKWEB':
            case 'darkweb': return 'ri-spy-line';
            case 'FORUM':
            case 'forum': return 'ri-chat-3-line';
            case 'SOCIAL_MEDIA':
            case 'social_media': return 'ri-share-line';
            case 'MARKETPLACE':
            case 'marketplace': return 'ri-store-line';
            case 'EMAIL':
            case 'email': return 'ri-mail-line';
            case 'WEBSITE':
            case 'website': return 'ri-global-line';
            case 'OTHER':
            case 'other': return 'ri-file-text-line';
            default: return 'ri-file-text-line';
        }
    };

    const getThreatIcon = (threatType: string | null) => {
        switch (threatType) {
            case 'DATA_BREACH':
            case 'data_breach': return 'ri-database-2-line';
            case 'MALWARE':
            case 'malware': return 'ri-bug-line';
            case 'PHISHING':
            case 'phishing': return 'ri-fishing-line';
            case 'RANSOMWARE':
            case 'ransomware': return 'ri-lock-line';
            case 'CREDENTIAL_THEFT':
            case 'credential_theft': return 'ri-key-line';
            case 'FINANCIAL_FRAUD':
            case 'financial_fraud': return 'ri-bank-line';
            case 'VULNERABILITY':
            case 'vulnerability': return 'ri-shield-cross-line';
            case 'SOCIAL_ENGINEERING':
            case 'social_engineering': return 'ri-user-voice-line';
            case 'DDOS_ATTACK':
            case 'ddos_attack': return 'ri-spam-line';
            case 'INSIDER_THREAT':
            case 'insider_threat': return 'ri-user-unfollow-line';
            case 'APT':
            case 'apt': return 'ri-spy-line';
            case 'OTHER':
            case 'other': return 'ri-alert-line';
            default: return 'ri-alert-line';
        }
    };

    const handleReportClick = (report: Report) => {
        setSelectedReport(report);
        setIsDetailModalOpen(true);
    };

    const closeModal = () => {
        setSelectedReport(null);
        setIsDetailModalOpen(false);
    };

    if (loading) {
        return (
            <DashboardLayout title="Threat Intelligence Reports">
                <div className="dashboard-reports-loading">
                    <div className="dashboard-reports-loading-spinner">
                        <i className="ri-loader-4-line"></i>
                    </div>
                    <div className="dashboard-reports-loading-text">Loading reports...</div>
                </div>
            </DashboardLayout>
        );
    }

    if (error) {
        return (
            <DashboardLayout title="Threat Intelligence Reports">
                <div className="dashboard-reports-error">
                    <div className="dashboard-reports-error-icon">
                        <i className="ri-error-warning-line"></i>
                    </div>
                    <div className="dashboard-reports-error-content">
                        <h3>Failed to Load Reports</h3>
                        <p>{error}</p>
                        <button
                            className="dashboard-reports-retry-btn"
                            onClick={() => window.location.reload()}
                        >
                            <i className="ri-refresh-line"></i>
                            Try Again
                        </button>
                    </div>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout title="Threat Intelligence Reports">
            <div className="dashboard-reports">
                {/* Header */}
                <div className="dashboard-reports-header">
                    <div className="dashboard-reports-header-content">
                        <h1 className="dashboard-reports-title">Threat Intelligence Reports</h1>
                        <p className="dashboard-reports-subtitle">
                            Access comprehensive threat intelligence reports and cybersecurity insights from various sources
                        </p>
                    </div>
                    <div className="dashboard-reports-stats">
                        <div className="dashboard-reports-stat">
                            <div className="dashboard-reports-stat-value">{allReports.length}</div>
                            <div className="dashboard-reports-stat-label">Total</div>
                        </div>
                        <div className="dashboard-reports-stat">
                            <div className="dashboard-reports-stat-value">
                                {allReports.length}
                            </div>
                            <div className="dashboard-reports-stat-label">Published</div>
                        </div>
                        <div className="dashboard-reports-stat">
                            <div className="dashboard-reports-stat-value">
                                {allReports.filter((r: Report) => {
                                    const score = getSafeScore(r.threatScore);
                                    return score && score >= 8;
                                }).length}
                            </div>
                            <div className="dashboard-reports-stat-label">High Risk</div>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="dashboard-reports-filters">
                    <div className="dashboard-reports-filters-row">
                        <div className="dashboard-reports-search-container">
                            <div className="dashboard-reports-search">
                                <i className="ri-search-line"></i>
                                <input
                                    type="text"
                                    placeholder="Search reports by title, description, platform, or threat type..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className={`dashboard-reports-search-input ${debouncedSearchQuery !== searchQuery ? 'searching' : ''}`}
                                />
                                {searchQuery && (
                                    <button
                                        className="dashboard-reports-search-clear"
                                        onClick={() => setSearchQuery('')}
                                        title="Clear search"
                                    >
                                        <i className="ri-close-line"></i>
                                    </button>
                                )}
                                {debouncedSearchQuery !== searchQuery && (
                                    <div className="dashboard-reports-search-loading">
                                        <i className="ri-loader-4-line rotating"></i>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    
                    <div className="dashboard-reports-filters-secondary">
                        <div className="dashboard-reports-filter-group">
                            <div className="dashboard-reports-filter-item">
                                <label className="dashboard-reports-filter-label">Platform</label>
                                <Select
                                    options={platformOptions}
                                    value={platformOptions.find(option => option.value === platformFilter)}
                                    onChange={(option) => {
                                        const newValue = option?.value || 'all';
                                        setPlatformFilter(newValue);
                                    }}
                                    placeholder="All"
                                    styles={selectStyles}
                                    isSearchable={false}
                                    className="dashboard-reports-filter-select"
                                />
                            </div>
                            
                            <div className="dashboard-reports-filter-item">
                                <label className="dashboard-reports-filter-label">Threat Type</label>
                                <Select
                                    options={threatTypeOptions}
                                    value={threatTypeOptions.find(option => option.value === threatTypeFilter)}
                                    onChange={(option) => {
                                        const newValue = option?.value || 'all';
                                        setThreatTypeFilter(newValue);
                                    }}
                                    placeholder="All"
                                    styles={selectStyles}
                                    isSearchable={false}
                                    className="dashboard-reports-filter-select"
                                />
                            </div>
                            
                            <div className="dashboard-reports-filter-actions">
                                <button
                                    className="dashboard-reports-filter-clear"
                                    onClick={() => {
                                        setSearchQuery('');
                                        setPlatformFilter('all');
                                        setThreatTypeFilter('all');
                                    }}
                                    title="Clear all filters"
                                    disabled={isFiltering}
                                >
                                    <i className="ri-filter-off-line"></i>
                                    Clear Filters
                                </button>
                                {isFiltering && (
                                    <div className="dashboard-reports-filter-loading">
                                        <i className="ri-loader-4-line rotating"></i>
                                        Filtering...
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Reports List */}
                <div className="dashboard-reports-list-container">
                    {paginatedReports.length === 0 ? (
                        <div className="dashboard-reports-empty">
                            <i className="ri-file-text-line"></i>
                            <h3>No reports found</h3>
                            <p>Try adjusting your search or filter criteria</p>
                        </div>
                    ) : (
                        <div className="dashboard-reports-list">
                            {paginatedReports.map((report) => (
                                <div 
                                    key={report.id} 
                                    className="dashboard-report-card"
                                    onClick={() => handleReportClick(report)}
                                >
                                    <div className="dashboard-report-main">
                                        <div className="dashboard-report-icon">
                                            <i className={getThreatIcon(report.threatType || null)}></i>
                                        </div>
                                        <div className="dashboard-report-content">
                                            <h4 className="dashboard-report-title">{report.title}</h4>
                                            <p className="dashboard-report-description">{report.description}</p>
                                            <div className="dashboard-report-meta">
                                                {report.platform && (
                                                    <span className="dashboard-report-platform">
                                                        <i className={getPlatformIcon(report.platform)}></i>
                                                        {report.platform.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase())}
                                                    </span>
                                                )}
                                                {report.affectedCompany && (
                                                    <span className="dashboard-report-company">
                                                        <i className="ri-building-line"></i>
                                                        {report.affectedCompany}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="dashboard-report-threat-type">
                                        <span className="dashboard-report-threat-type-badge">
                                            {report.threatType?.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown'}
                                        </span>
                                    </div>
                                    <div className="dashboard-report-score">
                                        {report.threatScore ? (
                                            <span
                                                className="dashboard-report-score-badge"
                                                style={{
                                                    backgroundColor: `${getThreatScoreColor(getSafeScore(report.threatScore))}20`,
                                                    color: getThreatScoreColor(getSafeScore(report.threatScore))
                                                }}
                                            >
                                                {(() => {
                                                    const score = getSafeScore(report.threatScore);
                                                    return score !== null ? score.toFixed(1) : report.threatScore;
                                                })()}
                                            </span>
                                        ) : (
                                            <span className="dashboard-report-score-badge dashboard-report-score-none">
                                                N/A
                                            </span>
                                        )}
                                    </div>
                                    <div className="dashboard-report-time">
                                        <span className="dashboard-report-time-main">{getTimeAgo(report.createdAt)}</span>
                                        <span className="dashboard-report-time-full">{formatDate(report.createdAt)}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="dashboard-reports-pagination">
                        <button
                            className="dashboard-reports-pagination-btn"
                            onClick={() => setCurrentPage(currentPage - 1)}
                            disabled={currentPage === 1}
                        >
                            <i className="ri-arrow-left-line"></i>
                            Previous
                        </button>
                        <div className="dashboard-reports-pagination-info">
                            Page {currentPage} of {totalPages} ({filteredReports.length} reports)
                        </div>
                        <button
                            className="dashboard-reports-pagination-btn"
                            onClick={() => setCurrentPage(currentPage + 1)}
                            disabled={currentPage === totalPages}
                        >
                            Next
                            <i className="ri-arrow-right-line"></i>
                        </button>
                    </div>
                )}

                {/* Report Detail Modal */}
                {isDetailModalOpen && selectedReport && (
                    <div className="dashboard-report-modal-overlay" onClick={closeModal}>
                        <div className="dashboard-report-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="dashboard-report-modal-header">
                                <div className="dashboard-report-modal-header-content">
                                    <h3>{selectedReport.title}</h3>
                                    <p>Threat Intelligence Report Details</p>
                                </div>
                                <button onClick={closeModal}>
                                    <i className="ri-close-line"></i>
                                </button>
                            </div>
                            <div className="dashboard-report-modal-content">
                                <div className="dashboard-report-modal-section">
                                    <h4>Report Information</h4>
                                    <div className="dashboard-report-modal-badges">
                                        {selectedReport.threatScore && (
                                            <span 
                                                className="dashboard-report-score-badge"
                                                style={{
                                                    backgroundColor: `${getThreatScoreColor(getSafeScore(selectedReport.threatScore))}20`,
                                                    color: getThreatScoreColor(getSafeScore(selectedReport.threatScore))
                                                }}
                                            >
                                                Threat Score: {(() => {
                                                    const score = getSafeScore(selectedReport.threatScore);
                                                    return score !== null ? score.toFixed(1) : selectedReport.threatScore;
                                                })()}
                                            </span>
                                        )}
                                        {selectedReport.threatType && (
                                            <span className="dashboard-report-threat-type-badge">
                                                <i className={getThreatIcon(selectedReport.threatType)}></i>
                                                {selectedReport.threatType.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase())}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                
                                <div className="dashboard-report-modal-section">
                                    <h4>Description</h4>
                                    <p>{selectedReport.description}</p>
                                </div>
                                
                                <div className="dashboard-report-modal-section">
                                    <h4>Content</h4>
                                    <div className="dashboard-report-modal-content-text">
                                        {selectedReport.content}
                                    </div>
                                </div>
                                
                                <div className="dashboard-report-modal-section">
                                    <h4>Details</h4>
                                    <div className="dashboard-report-modal-info">
                                        {selectedReport.platform && (
                                            <div className="dashboard-report-modal-info-item">
                                                <span className="dashboard-report-modal-info-label">Platform:</span>
                                                <span>
                                                    <i className={getPlatformIcon(selectedReport.platform)}></i>
                                                    {selectedReport.platform.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase())}
                                                </span>
                                            </div>
                                        )}
                                        {selectedReport.affectedCompany && (
                                            <div className="dashboard-report-modal-info-item">
                                                <span className="dashboard-report-modal-info-label">Affected Company:</span>
                                                <span>{selectedReport.affectedCompany}</span>
                                            </div>
                                        )}
                                        {selectedReport.postedBy && (
                                            <div className="dashboard-report-modal-info-item">
                                                <span className="dashboard-report-modal-info-label">Posted By:</span>
                                                <span>{selectedReport.postedBy}</span>
                                            </div>
                                        )}
                                        {selectedReport.dateDetected && (
                                            <div className="dashboard-report-modal-info-item">
                                                <span className="dashboard-report-modal-info-label">Date Detected:</span>
                                                <span>{formatDate(selectedReport.dateDetected)}</span>
                                            </div>
                                        )}
                                        {selectedReport.dataType && (
                                            <div className="dashboard-report-modal-info-item">
                                                <span className="dashboard-report-modal-info-label">Data Type:</span>
                                                <span>{selectedReport.dataType}</span>
                                            </div>
                                        )}
                                        <div className="dashboard-report-modal-info-item">
                                            <span className="dashboard-report-modal-info-label">Proof Shared:</span>
                                            <span>{selectedReport.proofShared ? 'Yes' : 'No'}</span>
                                        </div>
                                        <div className="dashboard-report-modal-info-item">
                                            <span className="dashboard-report-modal-info-label">Created:</span>
                                            <span>{formatDate(selectedReport.createdAt)}</span>
                                        </div>
                                        <div className="dashboard-report-modal-info-item">
                                            <span className="dashboard-report-modal-info-label">Created by:</span>
                                            <span>{selectedReport.createdBy?.name || 'Unknown'}</span>
                                        </div>
                                        {selectedReport.tags && selectedReport.tags.length > 0 && (
                                            <div className="dashboard-report-modal-info-item">
                                                <span className="dashboard-report-modal-info-label">Tags:</span>
                                                <div className="dashboard-report-modal-tags">
                                                    {selectedReport.tags.map((tag, index) => (
                                                        <span key={index} className="dashboard-report-tag">
                                                            {tag}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            
                            <div className="dashboard-report-modal-actions">
                                {selectedReport.downloadUrl && (
                                    <a 
                                        href={selectedReport.downloadUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="dashboard-report-modal-btn dashboard-report-modal-btn-primary"
                                    >
                                        <i className="ri-download-line"></i>
                                        Download
                                    </a>
                                )}
                                <button 
                                    className="dashboard-report-modal-btn dashboard-report-modal-btn-secondary"
                                    onClick={closeModal}
                                >
                                    <i className="ri-close-line"></i>
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </DashboardLayout>
    );
};

export default DashboardReports;
